import React, {
  useEffect,
  useState,
  useRef,
  useMemo,
} from "react";
import { connect } from "react-redux";
import { message, Input, DatePicker } from "antd";
import CSSTransitionGroup from "react-transition-group/CSSTransitionGroup";
import { Card, CardBody, Row, Col, Label, Button } from "reactstrap";
import { useHistory } from "react-router-dom";
import { withTranslation } from "react-i18next";
import * as configApi from '../../config/configApi.js';
import dayjs from "dayjs";
import { 
    API_ListPatientForLocation,
    API_ReportTicked_Base64 
} from "../../Api/AppBackend/ListPatient";
import CustomPage from "../../Components/CustomPage/CustomPage.js";
import "./style.css"
import { faPrint,faDownload, faFileExcel, faSearch } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import PrintJS from "print-js";

const ListPatient = (props) => {
    const dateFormatList = [
        "DD/MM/YYYY",
        "DD/MM/YY",
        "DD-MM-YYYY",
        "DD-MM-YY",
      ];
    const [dataSource, setDataSource] = useState([]);
    const [totalCost, setTotalCost] = useState(0);
    const [isExport, setIsExport]= useState(false);
    const [loading, setLoading] = useState(false);
    const { RangePicker } = DatePicker;
    const defaultDate = {
        FROM: dayjs().startOf("days"),
        TO: dayjs().endOf("days"),
      };

    const history = useHistory();
    useEffect(() => {
        const typeUser = Number(localStorage.getItem("typeUser"));
        if (![1,2,3].includes(typeUser)) {
        message.error(props.t("You do not have permission to access this page"));
        history.push("/");
        }
    }, [history]);

    const primaryColor = configApi.USE_COLOR;
    const isMobile = window.innerWidth <= 768;
    const [filter, setFilter] = useState({
        fromDate: defaultDate.FROM,
        toDate: defaultDate.TO,
        filterText: ""
    });

    const getDataPatients = async ()=>{
        if (!filter.fromDate || !filter.toDate || !dayjs.isDayjs(filter.fromDate) || !dayjs.isDayjs(filter.toDate)) {
            message.error(props.t("Invalid filter"));
            return;
        }
        try {
            setLoading(true)

            const payload = {
                ...filter,
                fromDate: filter.fromDate.format("YYYY-MM-DDTHH:mm:ss.SSS[Z]"),
                toDate: filter.toDate.format("YYYY-MM-DDTHH:mm:ss.SSS[Z]")
              };

            const res = await API_ListPatientForLocation(payload);
            if (res.data.isSuccess && Array.isArray(res.data.data)) {
                setDataSource(res.data.data);
                message.success(props.t("Data Refreshed Successfully"));
            }
            else{
                message.error(res.data.err.msgString);
            }
        } catch (error) {}
        finally{
        setLoading(false)
        }
    }
    const printSignaturePdf = async (row) => {
        try {
            if(isMobile){
                message.error(props.t("No support to view PDF on the phone"));
                return
            }

            setLoading(true);
            const requestBody = {
                sid: row._original.sid
              };
        
            const response = await API_ReportTicked_Base64(requestBody);
        
            if (response.data.isSuccess && response.data.item) {
              PrintJS({
                printable: response.data.item,
                type: 'pdf',
                base64: true
              });
            } else {
              message.error(response.data.err.msgString);
            }
        
        } catch (ex) {
        message.error(ex.message);
        }
        finally{
            setLoading(false);
        }
    }

    const downloadSignaturePdf = async (row) => {
        const defaultFileName = `${row._original.sid}_${row._original.patientName}.pdf`;
    
        const downloadFile = async (base64Data) => {
            const byteCharacters = atob(base64Data);
            const byteArray = new Uint8Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
                byteArray[i] = byteCharacters.charCodeAt(i);
            }
            const blob = new Blob([byteArray], { type: 'application/pdf' });
            return blob;
        };
    
        const handleFileSave = async (blob, fileHandle) => {
            try {
                const writable = await fileHandle.createWritable();
                await writable.write(blob);
                await writable.close();
                message.success(props.t("Downloaded signed PDF successfully"));
            } catch {
                message.error(props.t("An error occurred while saving the file"));
            }
        };
    
        const saveFileWithFallback = async (blob) => {
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = defaultFileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            message.success(props.t("Downloaded signed PDF successfully"));
        };
    
        try {
            setLoading(true);
            const response = await API_ReportTicked_Base64({ sid: row._original.sid });
    
            if (!response.data.isSuccess || !response.data.item) {
                message.error(response.data.err.msgString);
                return;
            }
    
            const blob = await downloadFile(response.data.item);
    
            if ('showSaveFilePicker' in window) {
                try {
                    const fileHandle = await window.showSaveFilePicker({
                        suggestedName: defaultFileName,
                        types: [{ description: 'PDF Document', accept: { 'application/pdf': ['.pdf'] } }]
                    });
                    await handleFileSave(blob, fileHandle);
                } catch (error) {
                    if (error.name !== 'AbortError') {
                        message.error(props.t("An error occurred while opening the file picker"));
                    }
                }
            } else {
                saveFileWithFallback(blob);
            }
    
        } catch (ex) {
            message.error(props.t("An unexpected error occurred: ") + ex.message);
        } finally {
            setLoading(false);
        }
    };

    const Columns = useMemo(
        () => [
        {
            filterable: false,
            resizable: false,
            sortable: false,
            width: 50,
            Header: `${props.t("No.")}`,
            Cell: (row) => {
                return <div style={{ width: "100%", textAlign: "center" }}>{row.index + 1}</div>;
            },
            headerStyle: {
                color: primaryColor
            },
        },
        {
            Header: `${props.t("Phone number")}`,
            filterable: true,
            accessor: "phone",
            width: 250,
            Cell: ({ value }) => {
                const phoneValue = value ? value.toString() : "";
                return <div style={{ width: "100%", textAlign: "center" }}>{phoneValue}</div>;
            },
            headerStyle: {
                color: primaryColor
            },
        },
        {
            Header: `${props.t("SID")}`,
            filterable: true,
            accessor: "sid",
            width: 250,
            Cell: ({ value }) => {
                const phoneValue = value ? value.toString() : "";
                return <div style={{ width: "100%", textAlign: "center" }}>{phoneValue}</div>;
            },
            headerStyle: {
                color: primaryColor
            },
        },
        {
            Header: `${props.t("Patient name")}`,
            filterable: true,
            accessor: "patientName",
            width: 300,
            headerStyle: {
                color: primaryColor
            },
        },
        {
            Header: `${props.t("Address")}`,
            filterable: true,
            accessor: "address",
            //width: 300,
            headerStyle: {
                color: primaryColor
            },
        },
        {
            Header: `${props.t("Total cost of testing")}`,
            filterable: true,
            accessor: "totalCost",
            width: 200,
            Cell: ({ value }) => value != null ? Math.round(value).toLocaleString() : 0,
            headerStyle: {
                color: primaryColor
            },
        },
        {
            Header: `${props.t("Invoice")}`,
            filterable: false,
            width: 150,
            Cell: (state) => {
                return (
                    <div className="invoice-icons">
                        <span title={props.t("View and print invoice")}>
                            <FontAwesomeIcon
                                icon={faPrint}
                                style={{color:primaryColor}}
                                className="invoice-icon"
                                onClick={(e) => {
                                    e.preventDefault();
                                    printSignaturePdf(state.row);
                                }}
                            />
                        </span>
                        <span title={props.t("Download invoice")}>
                            <FontAwesomeIcon
                                icon={faDownload}
                                style={{color:primaryColor}}
                                className="invoice-icon"
                                onClick={(e) => {
                                    e.preventDefault();
                                    downloadSignaturePdf(state.row);
                                }}
                            />
                        </span>
                    </div>
                );
            },
            headerStyle: {
                color: primaryColor
            },
        }
        ],
        [props]
    );

    useEffect(()=>{
        getDataPatients()
    }, [filter.fromDate, filter.toDate]);

    useEffect(()=>{
        const totalCostSum = dataSource.reduce((sum, item) => sum + item.totalCost, 0);
        setTotalCost(Math.round(totalCostSum).toLocaleString())
    }, [dataSource])
    return (
    <div>
        <div
            style={{
                backgroundColor: configApi.USE_COLOR,
                fontSize: "large",
                padding: 5,
            }}
        >
            <h4
                className="text-center"
                style={{ color: "white", fontSize: "1.4em" }}
            >
                {props.t('PATIENT LIST')}
            </h4>
        </div>
        <div className="filter-bar">
        <div className="filter-controls">
            <div className="label-patient"
                style={{
                    color:primaryColor
                }}
            >
              {props.t('Date of search  ')}
            </div>
            <RangePicker
            format={dateFormatList}
            size="middle"
            showTime={false}
            allowClear={false}
            value={[filter.fromDate, filter.toDate]}
            onChange={(val) => {
                setFilter((prev) => ({
                ...prev,
                fromDate: val?.[0] || null,
                toDate: val?.[1] || null,
                }));
            }}
            />
            <Input
                className="search-input"
                placeholder={props.t("Patient name, SID, phone number")}
                onChange={(e) =>
                setFilter((prev) => ({
                    ...prev,
                    filterText: e.target.value,
                }))
                }
                onPressEnter={getDataPatients}
            />
            <Button
            color="primary"
            type="submit"
            onClick={getDataPatients}
            style={{ backgroundColor: primaryColor, border:"none"}}
            >
            <FontAwesomeIcon icon={faSearch}/> {props.t("Search")}
            </Button>
            <Button
            color="primary"
            type="submit"
            onClick={
                () => setIsExport(true)
            }
            style={{ backgroundColor: primaryColor, border:"none" }}
            >
            <FontAwesomeIcon icon={faFileExcel}/> {props.t("Export excel")}
            </Button>
        </div>
        <div 
            className="total-cost-label"
            style={{
                color: primaryColor,
            }}>
                <span>{props.t("Total collection test costs")}:</span>
                <span style={{ fontSize: "25px" }}>{totalCost.toLocaleString()}</span>
        </div>
    </div>
    <div className="table-user-management">
        <CSSTransitionGroup
        component="div"
        transitionName="TabsAnimation"
        transitionAppear={true}
        transitionAppearTimeout={0}
        transitionEnter={false}
        transitionLeave={false}
        >
        <Row>
            <Col md="12" style={{ padding: 0 }}>
            <Card>
                <CardBody 
                    style={{ 
                        paddingBottom: "10px", 
                        paddingTop:"0px"
                    }}>
                <CustomPage
                    pageName={props.t('PATIENT LIST')}
                    data={dataSource}
                    columns={Columns}
                    modal={false}
                    isExportCSV={isExport}
                    setIsExportCSV={setIsExport}
                    onRefreshHandle={() => {
                        getDataPatients();
                    }}
                    loading={loading}
                    useRefresh={true}
                    useCusomCol={false}
                    tableStyle={{ 
                        height: isMobile ? "calc(100vh - 350px)" : "calc(100vh - 200px)",
                    }}
                ></CustomPage>
                </CardBody>
            </Card>
            </Col>
        </Row>
        </CSSTransitionGroup>
    </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  global: state.global,
});
export default connect(mapStateToProps)(
  withTranslation(["listpatient"])(ListPatient)
);
